import { useState } from 'react';
import type { ContactField, VaultItem } from '@heybesafe/shared';
import { HbsInput } from './hbs/Input';
import { HbsButton } from './hbs/Button';
import ContactFieldListField from './ContactFieldListField';

/** Drop blank rows and trim; keep only rows with a value so we never persist
 *  an empty `{label:'',value:''}`. Returns undefined when nothing remains so
 *  the field is omitted from the item entirely. */
function cleanFields(rows: ContactField[]): ContactField[] | undefined {
  const cleaned = rows
    .filter((r) => r.value.trim())
    .map((r) => ({ label: r.label.trim(), value: r.value.trim() }));
  return cleaned.length ? cleaned : undefined;
}

export interface ContactFormValues {
  name: string;
  role?: string;
  company?: string;
  website?: string;
  phones?: ContactField[];
  emails?: ContactField[];
  addresses?: ContactField[];
  notes?: string;
}

export interface ContactFormProps {
  heading: string;
  initial?: Partial<VaultItem>;
  isEdit: boolean;
  loading: boolean;
  onSubmit: (values: ContactFormValues) => void;
  onCancel: () => void;
}

export default function ContactForm({
  heading,
  initial,
  isEdit,
  loading,
  onSubmit,
  onCancel,
}: ContactFormProps): JSX.Element {
  const [name, setName] = useState(initial?.name ?? '');
  const [role, setRole] = useState(initial?.role ?? '');
  const [company, setCompany] = useState(initial?.company ?? '');
  const [website, setWebsite] = useState(initial?.website ?? '');
  const [phones, setPhones] = useState<ContactField[]>(initial?.phones ?? [{ label: 'Mobile', value: '' }]);
  const [emails, setEmails] = useState<ContactField[]>(initial?.emails ?? []);
  const [addresses, setAddresses] = useState<ContactField[]>(initial?.addresses ?? []);
  const [notes, setNotes] = useState(initial?.notes ?? '');

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      role: role.trim() || undefined,
      company: company.trim() || undefined,
      website: website.trim() || undefined,
      phones: cleanFields(phones),
      emails: cleanFields(emails),
      addresses: cleanFields(addresses),
      notes: notes.trim() || undefined,
    });
  }

  return (
    <div className="h-full flex flex-col max-w-3xl">
      <form id="contact-editor-form" onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto p-7 flex flex-col gap-4">
        <h2 className="font-display font-semibold text-[24px] tracking-hbs-tight text-hbs-text mb-2">
          {heading}
        </h2>
        <HbsInput label="Name" value={name} onChange={setName} placeholder="e.g. Bob the Plumber" required autoFocus name="name" recommended={!isEdit} />
        <HbsInput label="Role" value={role} onChange={setRole} placeholder="e.g. Plumber, Pediatrician" name="role" />
        <HbsInput label="Company" value={company} onChange={setCompany} placeholder="e.g. Bob's Plumbing" name="company" />

        <ContactFieldListField
          idPrefix="contact-phones"
          legend="Phones"
          values={phones}
          onChange={setPhones}
          labelSuggestions={['Mobile', 'Home', 'Work']}
          valuePlaceholder="(703) 555-1212"
          valueType="tel"
          addLabel="+ Add phone"
        />
        <ContactFieldListField
          idPrefix="contact-emails"
          legend="Emails"
          values={emails}
          onChange={setEmails}
          labelSuggestions={['Personal', 'Work']}
          valuePlaceholder="name@example.com"
          valueType="email"
          addLabel="+ Add email"
        />
        <ContactFieldListField
          idPrefix="contact-addresses"
          legend="Addresses"
          values={addresses}
          onChange={setAddresses}
          labelSuggestions={['Home', 'Work']}
          valuePlaceholder="1 Main St"
          addLabel="+ Add address"
        />

        <HbsInput label="Website" value={website} onChange={setWebsite} placeholder="example.com" name="website" />

        <div>
          <label
            htmlFor="contact-notes-input"
            className="block text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1"
          >
            Notes
          </label>
          <textarea
            id="contact-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes…"
            rows={3}
            className="w-full px-3 py-2 bg-hbs-surface border-[1.5px] border-hbs-border rounded-hbs-sm text-[15px] text-hbs-text placeholder:text-hbs-text-mute focus:outline-none focus-within:border-hbs-primary focus-within:ring-4 focus-within:ring-hbs-primary/20 resize-none"
          />
        </div>

      </form>
      <div className="shrink-0 border-t border-hbs-border bg-hbs-bg px-7 py-4 flex gap-2">
        <HbsButton id="contact-save-button" type="submit" form="contact-editor-form" loading={loading} fullWidth>
          Save
        </HbsButton>
        <HbsButton
          id="contact-cancel-button"
          type="button"
          variant="ghost"
          fullWidth
          onClick={onCancel}
        >
          Cancel
        </HbsButton>
      </div>
    </div>
  );
}
